import { selectDepartmentsById } from 'modules/employmentInfo/store/department/selectors';
import { getDisplayCustomFieldsValues, getCustomFieldsValueIds } from 'modules/subassignmentManagement/components/SubassignmentTable/store/selectors';
import React, { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { EntryType, IEntry } from 'shared/models/sheet/Sheet';
import GridTable from 'shared/components/table/GridTable/GridTable';
import { useSelector } from 'react-redux';
import { Box } from '@material-ui/core';
import clsx from 'clsx';
import { ICellInfo } from 'shared/components/table/GridTable/GridTableModel';
import { IEntryRow } from 'shared/components/table/EntriesTable/model';
import { getEntryErrorsById, getEntryRowIdByRow } from 'shared/components/table/EntriesTable/utils';
import { useEntriesTableStyles } from 'shared/components/table/EntriesTable/EntriesTableStyles';
import { getProjectAssignmentByProjectAndAssignmentIds } from 'shared/utils/helpers/entries';
import {
    selectActivitiesById,
    selectAssignmentsById,
    selectJobNumbersById,
    selectLocationsById,
    selectPositionsById,
    selectProjectsWithAssignments,
    selectTasksById,
} from 'store/entities/configuration/configurationSelectors';
import { selectCustomFieldValuesByIds, selectDisplayedOnTableCustomFields } from 'store/entities/customFields/selectors';
import { selectUsersById } from 'store/entities/users/selectors';
import { EntriesTableHeader } from './components/EntriesTableHeader';

interface IEntriesTableProps {
    date: string;
    entries: Array<IEntry>;
    entryFilter?: EntryType;
    cells: Array<ICellInfo<IEntryRow>>;
    hideHeader: boolean;
    isLoading: boolean;
    isFileEntriesView?: boolean;
    headerActionsEnabled?: boolean;
    collapsed?: boolean;
    isCollapsible?: boolean;
    userId?: string;
    isEntryDisabled?: (entry: IEntry) => boolean;
}

export default function EntriesTable({
    date,
    entries,
    entryFilter,
    cells,
    isLoading,
    hideHeader,
    userId,
    isEntryDisabled,
    isFileEntriesView = false,
    headerActionsEnabled = false,
    collapsed = false,
    isCollapsible = false,
}: IEntriesTableProps) {
    const classes = useEntriesTableStyles();

    const sortedEntries: Array<IEntry> = useMemo(
        () => [...entries].sort(
            (a, b) => moment(a.created_at).diff(moment(b.created_at)),
        ),
        [entries],
    );

    const assignmentsById = useSelector(selectAssignmentsById);
    const activitiesById = useSelector(selectActivitiesById);
    const usersByIds = useSelector(selectUsersById);
    const tasksById = useSelector(selectTasksById);
    const positionsById = useSelector(selectPositionsById);
    const locationsById = useSelector(selectLocationsById);
    const departmentsById = useSelector(selectDepartmentsById);
    const jobNumbersById = useSelector(selectJobNumbersById);
    const projectAssignments = useSelector(selectProjectsWithAssignments);
    const entryErrorById = getEntryErrorsById(sortedEntries);
    const customFieldValuesByIds = useSelector(selectCustomFieldValuesByIds);
    const tableCustomFields = useSelector(selectDisplayedOnTableCustomFields);

    // @ts-ignore
    const entriesRows: Array<IEntryRow> = sortedEntries.map(entry => {
        const projectAssignment = getProjectAssignmentByProjectAndAssignmentIds(
            projectAssignments,
            entry.project_id,
            entry.assignment_id,
        );
        const assignment = assignmentsById[entry.assignment_id];
        return {
            entry,
            userId,
            projectAssignment,
            location: locationsById[entry.location_id ?? ''],
            position: positionsById[entry.position_id ?? ''],
            department: departmentsById[entry.department_id ?? ''],
            assignment: assignmentsById[entry.assignment_id],
            activity: activitiesById[entry.activity_id ?? ''],
            jobNumber: jobNumbersById[entry.job_number_id ?? ''],
            task: tasksById[entry.task_id],
            error: entryErrorById[entry.id],
            employee: usersByIds[assignment?.user_id],
            displayCustomFieldValues: getDisplayCustomFieldsValues(
                tableCustomFields,
                customFieldValuesByIds,
                entry.custom_field_value_ids || [],
            ),
            customFieldValuesIds: getCustomFieldsValueIds(
                tableCustomFields,
                customFieldValuesByIds,
                entry.custom_field_value_ids || [],
            ),
            disabled: isEntryDisabled ? isEntryDisabled(entry) : false,
        };
    });

    const timeSheetIds = useMemo(() => {
        return [...new Set(entries.filter(entry => entry.entry_type === EntryType.TIME).map(entry => entry.sheet_id))];
    }, [entries]);
    const isPerDiem = useMemo(() => {
        return !!entries.find(entry => entry.is_per_diem);
    }, [entries]);
    const isHoliday = useMemo(() => {
        return !!entries.find(entry => entry.is_holiday_time);
    }, [entries]);

    const [currentCollapsed, setCollapsed] = useState(collapsed);
    const collapseSheetsHandler = useCallback(() => {
        setCollapsed(!currentCollapsed);
    }, [currentCollapsed]);

    const isCollapsed = isCollapsible && currentCollapsed;
    const getRowClasses = useCallback((row: IEntryRow) => {
        if (row.disabled) {
            return [classes.disabled];
        }
        return [];
    }, [classes]);

    return isLoading || entriesRows.length > 0 ? (
        <>
            <Box>
                {!isLoading && (
                    <EntriesTableHeader
                        date={date}
                        entryFilter={entryFilter}
                        entries={sortedEntries}
                        timeSheetIds={timeSheetIds}
                        isFileView={isFileEntriesView}
                        isPerDiem={isPerDiem}
                        isHoliday={isHoliday}
                        actionsEnabled={headerActionsEnabled}
                        onCollapse={collapseSheetsHandler}
                        collapsed={currentCollapsed}
                        isCollapsible={isCollapsible}
                    />
                )}
                {!isCollapsed && (
                    <Box className={clsx(
                        isLoading ? classes.tableWrapperLoading : '',
                    )}>
                        <GridTable
                            getKey={row => row.entry.id}
                            getRowId={getEntryRowIdByRow}
                            headerCellClassName={classes.headerCellClassName}
                            isLoading={isLoading}
                            rowData={entriesRows}
                            cells={cells}
                            hideHeader={hideHeader}
                            getRowClasses={getRowClasses}
                            stickyHeader
                        />
                    </Box>
                )}
            </Box>
        </>
    ) : null;
}
